import { HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CommunicationService } from '../../../communication/communication.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfirmationDialogComponent } from '../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ProjectChecklist, ProjectInfo } from '../../../models/project/project-info.model';
import Utils from '../../../util';
import { ChecklistTemplateNameDialogComponent } from './checklist-template-name-dialog/checklist-template-name-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ProjectChecklistService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();

  checklistsLoadedNotifier = new BehaviorSubject<boolean>(false);
  checklistsSavedNotifier = new BehaviorSubject<boolean>(false);
  checklistsModifiedNotifier = new BehaviorSubject<boolean>(false);
  checklistTemplatesLoadedNotifier = new BehaviorSubject<boolean>(false);
  checklistFormTemplatesLoadedNotifier = new BehaviorSubject<boolean>(false);
  checklistFormTemplateLoadedNotifier = new EventEmitter();
  checklistFormTemplatesUpdatedNotifier = new EventEmitter();
  checklistsConvertedNotifier = new EventEmitter();
  formSavedToChecklistNotifier = new Subject<boolean>();

  projectInfo: ProjectInfo;
  checklistTemplates: any[] = [];           // {name: string, templateid: string}
  checklistFormTemplates: any[] = [];       // {name: string, templateid: string, formType: string, created: Date}
  inProgressTemplate: ProjectChecklist[] = [];

  isProjectInput = false;

  constructor(
    private projectCommService: ProjectCommunicationService,
    private toastrService: ConfiguredToastrService,
    private comService: CommunicationService,
    private dialog: MatDialog,
    private translationService: AnessaTranslationService,
    private router: Router
  ) {
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.projectInfo = this.projectCommService.getProjectInfo();
          if (this.router.routerState.snapshot.url.indexOf('create-template') > 0) {
            this.projectInfo.checklists = this.inProgressTemplate;
          }
          this.checklistsLoadedNotifier.next(true);
        }
      });

    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.projectInfo = this.projectCommService.getProjectInfo();
          this.checklistsSavedNotifier.next(true);
        }
      });

    // get templates of the account if available
    this.comService.getAllRequest('/wsasapi/checklist/getNames')
      .pipe(take(1))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(
        (data) => {
          this.checklistTemplates = data['obj'];
          this.checklistTemplatesLoadedNotifier.next(true);
        }
      );

    this.comService.getAllRequest('/wsasapi/checklist/getFormTemplates')
      .pipe(take(1))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((res: HttpResponse<any>) => {
        if (res) {
          this.checklistFormTemplates = this.parseTemplateData(res['obj']);
          this.checklistFormTemplatesLoadedNotifier.next(true);
        }
      });

    this.projectCommService.projectInputDraftSaveNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(save => {
        if (save) {
          if (this.router.routerState.snapshot.url.indexOf('create-template') > 0) {
            this.inProgressTemplate = ProjectChecklist.arrayDeepCopy(this.projectInfo.checklists);
          }
        }
      });

    this.formSavedToChecklistNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(save => {
        if (save) {
          if (this.router.routerState.snapshot.url.indexOf('create-template') > 0) {
            this.inProgressTemplate = ProjectChecklist.arrayDeepCopy(this.projectInfo.checklists);
          }
        }
      });
  }

  /**
   * Parses an array of checklist template data, converting some values to what we need.
   * @param templateData array of template data, from database preferrably
   * @returns array of template data with some values converted like date
   */
  private parseTemplateData(templateData: any[]) {
    return templateData.map(template => this.parseTemplate(template));
  }

  /**
   * Parse a single template object to the custom specifications we want.
   * @param data single template data object
   * @returns "same" template object with some values changed, mainly date
   */
  private parseTemplate(data: Object) {
    let template = {};
    template['name'] = data['name'];
    template['templateid'] = data['templateid'] || data['_id'];
    if (data['formType']) {
      if (data['formType'] === 'form') {
        template['formType'] = 'PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.FORM';
      } else {
        template['formType'] = 'PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.TABS';
      }
    } else {
      template['formType'] = '';
    }
    if (data['created']) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' } as const;
      template['created'] = new Date(data['created']).toLocaleDateString('en-US', options)
    } else {
      template['created'] = '';
    }
    return template;
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  getChecklists(): ProjectChecklist[] {
    if (this.projectInfo.checklists) {
      return ProjectChecklist.arrayDeepCopy(this.projectInfo.checklists);
    }
    return [];
  }

  getChecklist(index: number): ProjectChecklist {
    if (index > -1 && index < this.projectInfo.checklists.length) {
      return ProjectChecklist.deepCopy(this.projectInfo.checklists[index]);
    }
    return null;
  }

  getChecklistNames() {
    if (!this.projectInfo || !this.projectInfo.checklists) return [];
    const names: string[] = [];
    for (const checklist of this.projectInfo.checklists) {
      names.push(checklist.name);
    }
    return names;
  }

  getChecklistTemplates() {
    return this.checklistTemplates;
  }

  addChecklist(checklist: ProjectChecklist) {
    const projectInfo = this.getProjectInfo();
    if (checklist && Utils.nameIsUnique(checklist.name, projectInfo.checklists)) {
      projectInfo.checklists.push(checklist);
      this.checkSaveProject(projectInfo);

      this.checklistsModifiedNotifier.next(true);
    }
  }

  editChecklist(index: number, checklist: ProjectChecklist) {
    const projectInfo = this.getProjectInfo();
    if (index > -1 && index < projectInfo.checklists.length && Utils.nameIsUnique(checklist.name, projectInfo.checklists, index)) {
      projectInfo.checklists.splice(index, 1, checklist);
      this.checkSaveProject(projectInfo);

      this.checklistsModifiedNotifier.next(true);
    }
  }

  editChecklists(checklists: ProjectChecklist[], notify = true) {
    const projectInfo = this.getProjectInfo();
    projectInfo.checklists = [];
    for (const list of checklists) {
      projectInfo.checklists.push(new ProjectChecklist(list));
    }
    this.checkSaveProject(projectInfo);

    if (notify) {
      this.checklistsModifiedNotifier.next(true);
    }
  }

  removeChecklist(index: number) {
    const projectInfo = this.getProjectInfo();
    if (index > -1 && index < projectInfo.checklists.length) {
      projectInfo.checklists.splice(index, 1);
      this.checkSaveProject(projectInfo);

      this.checklistsModifiedNotifier.next(true);
    }
  }

  getProjectInfo() {
    if (this.isProjectInput) {
      return ProjectInfo.deepCopy(this.projectInfo);
    } else {
      return this.projectCommService.getProjectInfo();
    }
  }

  checkSaveProject(projectInfo: ProjectInfo) {
    if (this.isProjectInput) {
      this.projectInfo = projectInfo;
    } else {
      this.projectCommService.setProjectInfo(projectInfo);
    }
  }

  clearChecklists() {
    this.projectInfo.checklists = [];
  }

  saveTemplate(myChecklist: ProjectChecklist, myName: string) {
    const toSave = Utils.deepCopy(myChecklist);
    toSave.name = myName;
    this.comService.postRequest({ checklist: toSave }, '/wsasapi/checklist/saveTemplate')
      .pipe(take(1))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(result => {
        if (result) {
          // append new template to exising ones so we don't have to reload them
          const templateid = result['obj']['_id'];
          this.checklistTemplates.push({ name: result['obj']['checklist']['name'], templateid: templateid });
          this.toastrService.success(this.translationService.get(
            'PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.SAVE_TEMPLATE_SUCCESS'
          ));

        }
        return result;
      });
  }

  getIsSubmittedProject() {
    return this.projectInfo.isSubmittedProject;
  }

  async loadTemplate(loadID: string): Promise<ProjectChecklist> {
    const result = await this.comService.getRequest(loadID, '/wsasapi/checklist/getTemplate').toPromise();
    return new ProjectChecklist(result['obj']['checklist']);
  }

  saveFormTemplate(checklists: ProjectChecklist[], templateName: string) {
    this.comService.postRequest({ templateName: templateName, checklists: checklists }, '/wsasapi/checklist/saveFormTemplate')
      .pipe(take(1))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(result => {
        if (result) {
          // append new template to exising ones so we don't have to reload them
          this.checklistFormTemplates.push(this.parseTemplate(result['obj']));
          this.checklistFormTemplatesUpdatedNotifier.emit();
          this.toastrService.success(this.translationService.get(
            'PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.SAVE_FORM_TEMPLATE_SUCCESS'
          ));
        }
        return result;
      });
  }

  async loadFormTemplate(templateId: string): Promise<ProjectChecklist[]> {
    const result = await firstValueFrom(this.comService.getRequest(templateId, '/wsasapi/checklist/getFormTemplate'));
    this.toastrService.success(this.translationService.get(
      'PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.LOAD_FORM_TEMPLATE_SUCCESS'
    ));
    const template = ProjectChecklist.arrayDeepCopy(result['obj']['checklists']);
    this.projectInfo.checklists = template;
    this.checklistFormTemplateLoadedNotifier.emit();
    return template;
  }

  onFormTemplateDelete(templateID: string) {
    const templateName = this.checklistFormTemplates.find(element => element.templateid === templateID).name;
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.DELETE_TEMPLATE_CONFIRMATION') + ' ' + templateName }
    });

    confirmDialog.afterClosed().pipe(take(1)).pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((result) => {
        if (result) this.deleteFormTemplate(templateID);
      });
  }

  deleteFormTemplate(templateId: string) {
    this.comService.deleteRequest(templateId, '/wsasapi/checklist/deleteFormTemplate')
      .pipe(take(1))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(
        (response) => {
          console.log(response);
          this.checklistFormTemplates = this.checklistFormTemplates.filter(item => item.templateid !== templateId);
          this.checklistFormTemplatesUpdatedNotifier.emit();
          this.toastrService.success(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.DELETE_FORM_TEMPLATE_SUCCESS'));
        }
      );
  }

  getChecklistFormTemplates() {
    return this.checklistFormTemplates;
  }

  onSaveFormTemplate() {
    const checklistsCopy = ProjectChecklist.arrayDeepCopy(this.projectInfo.checklists);
    const nameDialog = this.dialog.open(ChecklistTemplateNameDialogComponent, {
      width: '400px',
      data: {
        forbiddenNames: this.getFormTemplateNameList()
      }
    });
    nameDialog.afterClosed().pipe(take(1)).subscribe(
      (response: string) => {
        if (!response) {
          return;
        }
        this.saveFormTemplate(checklistsCopy, response);
      }
    );
  }

  private getFormTemplateNameList() {
    const names = [];
    for (const template of this.checklistFormTemplates) {
      names.push(template.name);
    }
    return names;
  }

  /**
   * Set the form display method of an array of checklists.
   * @param formType 'form' or 'tabs'
   * @param lists optional, convert the supplied lists or use the ones currently loaded in service
   * @returns array of checklists with a `displayMethod` property added to each checklist
   */
  convertTo(formType: string, lists?: ProjectChecklist[]) {
    if (formType === 'form' || formType === 'tabs') {
      const checklists = lists || this.getChecklists();
      for (const checklist of checklists) {
        checklist.displayMethod = formType;
        for (const row of checklist.rows) {
          row.columnSettings = [];
        }
      }
      this.projectInfo.checklists = checklists;
      this.checklistsConvertedNotifier.emit(checklists);
      return checklists;
    } else {
      throw new Error('invalid form conversion type: ' + formType);
    }
  }
}
